// =============================================================================
// Origin UI — src/pages/approvals/ApprovalsInboxPage.tsx
// Unified Approvals Inbox — combines onboarding/offboarding/transfer requests
// with leave requests into a single view. Includes delegation management.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/apiService';
import { LoadingSpinner, EmptyState, PageHeader, Card, ErrorBanner, ConfirmDialog } from '@/components/common';
import styles from './approvals.module.css';

// ─── Types ───

interface UnifiedApprovalItem {
  id: string;
  approvalType: string;
  employeeName: string;
  department: string;
  officeLocation: string;
  submittedAt: string;
  effectiveDate: string;
  summary: string;
  urgencyLevel: string;
  isDelegated: boolean;
  delegatedOnBehalfOf: string | null;
  delegatorEmployeeId: string | null;
  availableActions: string[];
}

interface UnifiedApprovalHistoryItem {
  id: string;
  approvalType: string;
  employeeName: string;
  department: string;
  officeLocation: string;
  submittedAt: string;
  effectiveDate: string;
  summary: string;
  decision: string;   // Approved | Rejected | Returned
  decidedAt: string;
  comment: string | null;
}

interface ApprovalDelegation {
  delegationId: string;
  delegatorId: string;
  delegatorName: string | null;
  delegateId: string;
  delegateName: string | null;
  startDate: string;
  endDate: string;
  reason: string | null;
  isActive: boolean;
  createdAt: string;
}

interface TeamMember {
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  departmentName: string;
  officeName: string;
}

// ─── Colour maps ───

const typePillColours: Record<string, { bg: string; text: string }> = {
  Onboarding: { bg: '#E0F7F8', text: '#009BA3' },
  Offboarding: { bg: '#FDE8E7', text: '#C33A30' },
  Transfer: { bg: '#E8EDF4', text: '#0A2240' },
  LeaveRequest: { bg: '#FFF3E0', text: '#E87722' },
  CreditCard: { bg: '#F1F4F6', text: '#64748B' },
  LieuClaim: { bg: '#E6F7F0', text: '#00794F' },
};

const urgencyStyles: Record<string, { bg: string; text: string; label: string }> = {
  overdue: { bg: '#FDE8E7', text: '#F04B41', label: 'Overdue' },
  urgent: { bg: '#FFF3E0', text: '#E87722', label: 'Urgent' },
  soon: { bg: '#FEF9E7', text: '#B8860B', label: 'Soon' },
  normal: { bg: '#F1F4F6', text: '#6B7885', label: 'Normal' },
};

const typeLabels: Record<string, string> = {
  Onboarding: 'Onboarding',
  Offboarding: 'Offboarding',
  Transfer: 'Transfer',
  LeaveRequest: 'Leave Request',
  CreditCard: 'Credit Card',
  LieuClaim: 'Day in Lieu',
};

const decisionStyles: Record<string, { bg: string; text: string }> = {
  Approved: { bg: '#E3F4E9', text: '#1B7F3B' },
  Rejected: { bg: '#FDE8E7', text: '#C33A30' },
  Returned: { bg: '#FEF9E7', text: '#B8860B' },
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ApprovalsInboxPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<UnifiedApprovalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pending | History tab
  const [view, setView] = useState<'pending' | 'history'>('pending');
  const [historyItems, setHistoryItems] = useState<UnifiedApprovalHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Filters
  const [filterType, setFilterType] = useState<string>('all');
  const [filterSource, setFilterSource] = useState<string>('all');

  // Delegation state
  const [showDelegation, setShowDelegation] = useState(false);
  const [myDelegations, setMyDelegations] = useState<ApprovalDelegation[]>([]);
  const [delegationsToMe, setDelegationsToMe] = useState<ApprovalDelegation[]>([]);
  const [delegateForm, setDelegateForm] = useState({ delegateId: '', startDate: '', endDate: '', reason: '' });
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [delegationLoading, setDelegationLoading] = useState(false);
  const [delegationError, setDelegationError] = useState('');
  const [revokeId, setRevokeId] = useState<string | null>(null);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      setLoading(true);
      const data = await api.get<UnifiedApprovalItem[]>('/api/approvals/unified');
      setItems(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    try {
      setHistoryLoading(true);
      setError('');
      const data = await api.get<UnifiedApprovalHistoryItem[]>('/api/approvals/history');
      setHistoryItems(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load approval history');
    } finally {
      setHistoryLoading(false);
    }
  }

  function switchView(v: 'pending' | 'history') {
    setView(v);
    if (v === 'history') loadHistory();
  }

  async function loadDelegations() {
    try {
      setDelegationLoading(true);
      const [mine, toMe, members] = await Promise.all([
        api.get<ApprovalDelegation[]>('/api/approvals/delegations/mine'),
        api.get<ApprovalDelegation[]>('/api/approvals/delegations/to-me'),
        api.get<TeamMember[]>('/api/approvals/delegations/candidates'),
      ]);
      setMyDelegations(mine);
      setDelegationsToMe(toMe);
      setTeamMembers(members);
    } catch (err: any) {
      setDelegationError(err.message);
    } finally {
      setDelegationLoading(false);
    }
  }

  function toggleDelegation() {
    if (!showDelegation) loadDelegations();
    setShowDelegation(!showDelegation);
  }

  async function createDelegation() {
    try {
      setDelegationError('');
      await api.post('/api/approvals/delegations', {
        delegateId: delegateForm.delegateId,
        startDate: delegateForm.startDate,
        endDate: delegateForm.endDate,
        reason: delegateForm.reason || null,
      });
      setDelegateForm({ delegateId: '', startDate: '', endDate: '', reason: '' });
      await loadDelegations();
      await loadAll();
    } catch (err: any) {
      const body = err.body ? JSON.parse(err.body) : null;
      setDelegationError(body?.error || err.message);
    }
  }

  async function handleRevoke() {
    if (!revokeId) return;
    try {
      await api.delete(`/api/approvals/delegations/${revokeId}`);
      setRevokeId(null);
      await loadDelegations();
      await loadAll();
    } catch (err: any) {
      setDelegationError(err.message);
      setRevokeId(null);
    }
  }

  // ─── Filter logic ───
  const filtered = items.filter(item => {
    if (filterType !== 'all' && item.approvalType !== filterType) return false;
    if (filterSource === 'direct' && item.isDelegated) return false;
    if (filterSource === 'delegated' && !item.isDelegated) return false;
    return true;
  });

  // Active delegations to me (for banner)
  const activeDelegationsToMe = delegationsToMe.filter(d => d.isActive);

  if (loading) return <LoadingSpinner message="Loading approvals..." />;

  return (
    <div>
      <PageHeader
        title="Approvals Inbox"
        subtitle={view === 'history'
          ? 'Decisions you have actioned'
          : items.length > 0
          ? `${items.length} item${items.length !== 1 ? 's' : ''} awaiting your decision`
          : 'No pending approvals'}
        action={
          <button className={styles.delegationToggle} onClick={toggleDelegation}>
            {showDelegation ? 'Hide Delegations' : 'Manage Delegations'}
          </button>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Active delegation banners */}
      {activeDelegationsToMe.map(d => (
        <div key={d.delegationId} className={styles.delegationBanner}>
          <span className={styles.delegationBannerIcon}>👤</span>
          You are acting as delegate for <strong>{d.delegatorName}</strong> until {new Date(d.endDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
      ))}

      {/* Delegation management panel */}
      {showDelegation && (
        <Card className={styles.delegationPanel}>
          <h4 className={styles.delegationTitle}>Delegation Management</h4>
          {delegationError && <ErrorBanner message={delegationError} onDismiss={() => setDelegationError('')} />}

          {delegationLoading ? <LoadingSpinner /> : (
            <>
              {/* My delegations */}
              {myDelegations.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div className={styles.delegationSubtitle}>My Active Delegations</div>
                  {myDelegations.map(d => (
                    <div key={d.delegationId} className={styles.delegationRow}>
                      <div>
                        <strong>{d.delegateName}</strong>
                        <span className={styles.delegationDates}>{d.startDate} → {d.endDate}</span>
                        {d.reason && <span className={styles.delegationReason}>{d.reason}</span>}
                      </div>
                      {d.isActive && (
                        <button className={styles.revokeBtn} onClick={() => setRevokeId(d.delegationId)}>Revoke</button>
                      )}
                      {!d.isActive && <span className={styles.delegationExpired}>Expired</span>}
                    </div>
                  ))}
                </div>
              )}

              {/* Delegations to me */}
              {delegationsToMe.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div className={styles.delegationSubtitle}>Delegated to Me</div>
                  {delegationsToMe.map(d => (
                    <div key={d.delegationId} className={styles.delegationRow}>
                      <div>
                        <strong>{d.delegatorName}</strong>
                        <span className={styles.delegationDates}>{d.startDate} → {d.endDate}</span>
                        {d.reason && <span className={styles.delegationReason}>{d.reason}</span>}
                      </div>
                      <span className={d.isActive ? styles.delegationActive : styles.delegationExpired}>
                        {d.isActive ? 'Active' : 'Expired'}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Create new delegation */}
              <div className={styles.delegationSubtitle}>+ New Delegation</div>
              <div className={styles.delegationForm}>
                <select
                  className={styles.delegationSelect}
                  value={delegateForm.delegateId}
                  onChange={e => setDelegateForm({ ...delegateForm, delegateId: e.target.value })}
                >
                  <option value="">Select delegate...</option>
                  {teamMembers.map(m => (
                    <option key={m.employeeId} value={m.employeeId}>
                      {m.employeeName} ({m.employeeNumber})
                    </option>
                  ))}
                </select>
                <input type="date" className={styles.delegationInput} value={delegateForm.startDate}
                  onChange={e => setDelegateForm({ ...delegateForm, startDate: e.target.value })} />
                <input type="date" className={styles.delegationInput} value={delegateForm.endDate}
                  onChange={e => setDelegateForm({ ...delegateForm, endDate: e.target.value })} />
                <input type="text" className={styles.delegationInput} placeholder="Reason (optional)"
                  value={delegateForm.reason}
                  onChange={e => setDelegateForm({ ...delegateForm, reason: e.target.value })} />
                <button className={styles.delegationCreateBtn} onClick={createDelegation}
                  disabled={!delegateForm.delegateId || !delegateForm.startDate || !delegateForm.endDate}>
                  Create
                </button>
              </div>
            </>
          )}
        </Card>
      )}

      {/* Tabs: Pending | History */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #E2E8F0' }}>
        {(['pending', 'history'] as const).map(v => (
          <button
            key={v}
            onClick={() => switchView(v)}
            style={{
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 14,
              color: view === v ? '#0A2240' : '#6B7885',
              borderBottom: view === v ? '2px solid #FF8300' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {v === 'pending' ? 'Pending' : 'History'}
          </button>
        ))}
      </div>

      {view === 'pending' && (
        <>
      {/* Filter controls */}
      <div className={styles.filterBar}>
        <select className={styles.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">All Types</option>
          <option value="Onboarding">Onboarding</option>
          <option value="Offboarding">Offboarding</option>
          <option value="Transfer">Transfer</option>
          <option value="LeaveRequest">Leave Request</option>
          <option value="CreditCard">Credit Card</option>
          <option value="LieuClaim">Day in Lieu</option>
        </select>
        <select className={styles.filterSelect} value={filterSource} onChange={e => setFilterSource(e.target.value)}>
          <option value="all">All Sources</option>
          <option value="direct">My Approvals</option>
          <option value="delegated">Delegated to Me</option>
        </select>
        <span className={styles.filterCount}>{filtered.length} of {items.length} items</span>
      </div>

      {/* Approvals table */}
      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon="✅"
            title="All caught up"
            message="There are no items awaiting your approval right now."
          />
        </Card>
      ) : (
        <Card padding={false}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Employee</th>
                  <th>Summary</th>
                  <th>Department</th>
                  <th>Effective Date</th>
                  <th>Urgency</th>
                  <th>Source</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => {
                  const tp = typePillColours[item.approvalType] || typePillColours.Onboarding;
                  const urg = urgencyStyles[item.urgencyLevel] || urgencyStyles.normal;

                  return (
                    <tr
                      key={`${item.approvalType}-${item.id}`}
                      className={styles.tableRow}
                      onClick={() => {
                        if (item.approvalType === 'LeaveRequest') {
                          navigate(`/approvals/leave/${item.id}`);
                        } else if (item.approvalType === 'LieuClaim') {
                          navigate(`/approvals/lieu/${item.id}`);
                        } else if (item.approvalType === 'CreditCard') {
                          navigate('/credit-cards/approvals');
                        } else {
                          navigate(`/approvals/${item.id}`);
                        }
                      }}
                    >
                      <td>
                        <span className={styles.typePill} style={{ background: tp.bg, color: tp.text }}>
                          {typeLabels[item.approvalType] || item.approvalType}
                        </span>
                      </td>
                      <td>
                        <div className={styles.employeeCell}>
                          <span className={styles.employeeName}>{item.employeeName}</span>
                        </div>
                      </td>
                      <td className={styles.summaryCell}>{item.summary}</td>
                      <td className={styles.muted}>{item.department}</td>
                      <td className={styles.muted}>
                        {new Date(item.effectiveDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td>
                        <span className={styles.urgencyPill} style={{ background: urg.bg, color: urg.text }}>
                          {urg.label}
                        </span>
                      </td>
                      <td>
                        {item.isDelegated ? (
                          <span className={styles.delegatedPill} title={`On behalf of ${item.delegatedOnBehalfOf}`}>
                            Delegated
                          </span>
                        ) : (
                          <span className={styles.directPill}>Direct</span>
                        )}
                      </td>
                      <td>
                        <span className={styles.reviewLink}>Review →</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
        </>
      )}

      {view === 'history' && (
        historyLoading ? (
          <LoadingSpinner message="Loading history..." />
        ) : historyItems.length === 0 ? (
          <Card>
            <EmptyState
              icon="🗂️"
              title="No history yet"
              message="Approvals you action will appear here."
            />
          </Card>
        ) : (
          <Card padding={false}>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Employee</th>
                    <th>Summary</th>
                    <th>Department</th>
                    <th>Decision</th>
                    <th>Decided</th>
                  </tr>
                </thead>
                <tbody>
                  {historyItems.map(item => {
                    const tp = typePillColours[item.approvalType] || typePillColours.Onboarding;
                    const dec = decisionStyles[item.decision] || { bg: '#F1F4F6', text: '#6B7885' };
                    return (
                      <tr key={`${item.approvalType}-${item.id}-${item.decidedAt}`}>
                        <td>
                          <span className={styles.typePill} style={{ background: tp.bg, color: tp.text }}>
                            {typeLabels[item.approvalType] || item.approvalType}
                          </span>
                        </td>
                        <td>
                          <div className={styles.employeeCell}>
                            <span className={styles.employeeName}>{item.employeeName}</span>
                          </div>
                        </td>
                        <td className={styles.summaryCell}>
                          {item.summary}
                          {item.comment && (
                            <div className={styles.muted} style={{ fontSize: 12, marginTop: 2 }}>
                              “{item.comment}”
                            </div>
                          )}
                        </td>
                        <td className={styles.muted}>{item.department}</td>
                        <td>
                          <span className={styles.typePill} style={{ background: dec.bg, color: dec.text }}>
                            {item.decision}
                          </span>
                        </td>
                        <td className={styles.muted}>
                          {new Date(item.decidedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      <ConfirmDialog
        open={revokeId !== null}
        title="Revoke Delegation"
        message="Are you sure you want to revoke this delegation? The delegate will immediately lose access to your pending approvals."
        confirmLabel="Revoke"
        confirmVariant="danger"
        onConfirm={handleRevoke}
        onCancel={() => setRevokeId(null)}
      />
    </div>
  );
}
